import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { loadKakaoSdk } from "@/lib/kakaoMap";
import { searchReferenceAddress } from "../services/customAddressService";

// 좌표 미입력 시 전국이 보이는 중심/줌
const DEFAULT_CENTER = { lat: 36.5, lng: 127.5 };
const DEFAULT_LEVEL = 12;
// 지점을 찍을 때 쓰는 줌 (도로·건물 식별 가능)
const PIN_LEVEL = 3;

const isValidCoord = (lat: number, lng: number) =>
  Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0;

const PIN_HTML =
  '<div style="width:14px;height:14px;border-radius:50%;background:#2563eb;border:2px solid #fff;box-shadow:0 0 0 2px #2563eb66;transform:translate(-50%,-50%)"></div>';

interface CoordinatePickerProps {
  lat: number;
  lng: number;
  onChange: (lat: number, lng: number) => void;
}

/**
 * 카카오 지도 클릭으로 좌표를 찍는 픽커.
 * 기준 주소 검색은 지도 중심을 옮기기 위한 것일 뿐, 좌표를 자동으로 채우지 않는다 —
 * 보완 주소는 "검색에 없는 지점"이므로 관리자가 직접 찍은 위치만 신뢰한다.
 */
export function CoordinatePicker({ lat, lng, onChange }: CoordinatePickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<kakao.maps.Map | null>(null);
  const pinRef = useRef<kakao.maps.CustomOverlay | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [referenceKeyword, setReferenceKeyword] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // 지도 생성 (1회)
  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;

    loadKakaoSdk()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const hasCoord = isValidCoord(lat, lng);
        const map = new kakao.maps.Map(containerRef.current, {
          center: new kakao.maps.LatLng(
            hasCoord ? lat : DEFAULT_CENTER.lat,
            hasCoord ? lng : DEFAULT_CENTER.lng,
          ),
          level: hasCoord ? PIN_LEVEL : DEFAULT_LEVEL,
        });
        mapRef.current = map;

        kakao.maps.event.addListener(map, "click", (event) => {
          if (!event) return;
          const clicked = event.latLng;
          onChangeRef.current(
            Number(clicked.getLat().toFixed(7)),
            Number(clicked.getLng().toFixed(7)),
          );
        });
        setIsLoaded(true);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "지도 로딩 실패");
      });

    return () => {
      cancelled = true;
      pinRef.current?.setMap(null);
      pinRef.current = null;
      mapRef.current = null;
    };
    // 최초 중심만 props 를 쓰고 이후 이동은 아래 effect 가 담당
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 좌표 변경 → 핀 이동 (수동 입력·지도 클릭 모두 이 경로로 반영)
  useEffect(() => {
    const map = mapRef.current;
    if (!isLoaded || !map) return;

    if (!isValidCoord(lat, lng)) {
      pinRef.current?.setMap(null);
      return;
    }

    const position = new kakao.maps.LatLng(lat, lng);
    if (!pinRef.current) {
      pinRef.current = new kakao.maps.CustomOverlay({ position, content: PIN_HTML, zIndex: 10 });
    } else {
      pinRef.current.setPosition(position);
    }
    pinRef.current.setMap(map);
    map.setCenter(position);
  }, [isLoaded, lat, lng]);

  const handleReferenceSearch = async () => {
    const map = mapRef.current;
    if (!map || !referenceKeyword.trim()) return;
    setIsSearching(true);
    try {
      const results = await searchReferenceAddress(referenceKeyword);
      if (results.length === 0) {
        toast.info("기준 주소를 찾지 못했습니다. 더 넓은 지명으로 검색해 보세요.");
        return;
      }
      const first = results[0];
      map.setCenter(new kakao.maps.LatLng(first.lat, first.lng));
      map.setLevel(PIN_LEVEL);
      toast.success(`지도를 이동했습니다: ${first.label}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "기준 주소 검색 실패");
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input
          value={referenceKeyword}
          onChange={(e) => setReferenceKeyword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleReferenceSearch();
            }
          }}
          placeholder="기준 주소로 지도 이동 (예: 평택시 포승읍)"
          className="h-8 text-xs"
          disabled={!isLoaded}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0 text-xs"
          onClick={() => void handleReferenceSearch()}
          disabled={!isLoaded || isSearching}
        >
          {isSearching ? "검색 중" : "이동"}
        </Button>
      </div>

      <div className="relative h-72 w-full overflow-hidden rounded-md border border-gray-200 bg-gray-50">
        <div ref={containerRef} className="h-full w-full" />
        {loadError && (
          <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-xs text-red-600">
            {loadError}
            <br />
            위도·경도를 직접 입력해 저장할 수 있습니다.
          </div>
        )}
        {!loadError && !isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">
            지도 로딩 중...
          </div>
        )}
      </div>
      <p className="text-[11px] text-gray-500">
        지도를 클릭하면 좌표가 채워집니다. 기준 주소 검색은 지도 이동만 하며 좌표를 바꾸지 않습니다.
      </p>
    </div>
  );
}
